import {useQueries} from 'react-query'
import {fetchStudentGuardianDetails, linkGuardianToStudent, setPrimaryGuardian} from './api'
import {FieldArray, Form, Formik, FormikHelpers} from 'formik'
import toast from 'react-hot-toast'
import {handleApiError} from '../../../helpers/errorHandler'
import FormikControl from '../../../components/formikControl/FormikControl'
import * as Yup from 'yup'
import {RunnerBall} from '../../../components/FormLoader'
import {
  SetPrimaryGuardianPayload,
  Student,
  StudentGuardianLinkFormValues,
  StudentLinkedGuardian,
} from './type'
import {deleteGuardian, fetchGuardiansList} from '../guardian/api'
import {Button, Col, Row} from 'react-bootstrap'
import {GuardianBase} from '../guardian/type'
import UnlinkGuardianConfirm from './UnlinkGuardianConfirm'
import {useState} from 'react'
import {mapGuardianApiToFormData, mapGuardianFormToApiData} from './mapper'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

interface LinkGuardianFormProps {
  studentId: string
  initialValues: Student
  onAfterSubmit: () => void
}
const validationSchema = Yup.object().shape({
  guardians: Yup.array().of(
    Yup.object().shape({
      parentId: Yup.string().required('Parent is required'),
      relation: Yup.string().required('Relation is required'),
    })
  ),
  primaryGuardian: Yup.string().required('Primary Guardian is required'),
})

function LinkGuardianForm({initialValues: studentDetails, onAfterSubmit}: LinkGuardianFormProps) {
  const [parentForRemoval, setParentForRemoval] = useState<number | string | null>(null)

  const studentId = studentDetails.student_id

  const result = useQueries([
    {
      queryKey: 'guardian',
      queryFn: async () => {
        const data = await fetchStudentGuardianDetails(studentId)
        return data.data.filter((item) => item.parent_id !== null)
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
    {
      queryKey: 'guardians',
      queryFn: fetchGuardiansList,
      refetchOnWindowFocus: false,
    },
  ])

  const {
    data: linkedGuardians,
    isFetching: isFetchingLinkedGuardians,
    refetch: refetchLinkedGuardians,
  } = result[0]
  const {data: guardianList, isFetching: isFetchingGuardianList} = result[1]

  if (isFetchingLinkedGuardians || isFetchingGuardianList) {
    return <RunnerBall />
  }

  const formInitialValues = {
    guardians: [
      {
        parentId: '',
        relation: '',
      } as StudentGuardianLinkFormValues,
    ],
    primaryGuardian: '',
  }

  const formValues =
    linkedGuardians!?.length > 0
      ? {
          guardians: linkedGuardians!?.map((guardian) => mapGuardianApiToFormData(guardian)),
          primaryGuardian:
            linkedGuardians!
              ?.find((guardian) => String(guardian.is_primary) === '1')
              ?.parent_id.toString() || '',
        }
      : formInitialValues

  const submitHandler = async (
    values: {guardians: StudentGuardianLinkFormValues[]; primaryGuardian: string},
    formikHelpers: FormikHelpers<{
      guardians: StudentGuardianLinkFormValues[]
      primaryGuardian: string
    }>
  ) => {
    const {setSubmitting} = formikHelpers
    const toastId = toast.loading('Submitting...')

    setSubmitting(true)
    try {
      for (const guardian of values.guardians) {
        if (!isAlreadyLinkedGuardian(guardian.parentId)) {
          const payload = mapGuardianFormToApiData({...guardian, studentId})

          await linkGuardianToStudent(payload)
        }
      }
      const selectedPrimaryGuardian = guardianList?.data.find((guardian: GuardianBase) => {
        return guardian.parent_id === Number(values.primaryGuardian)
      })

      let earlyprimaryGuardian = linkedGuardians?.find(
        (guardian) => String(guardian.is_primary) === '1'
      )

      const primaryGuradianPayload: SetPrimaryGuardianPayload = {
        student_id: studentId,
        parent_id: selectedPrimaryGuardian?.parent_id,
        user_id: selectedPrimaryGuardian?.user_id,
        is_primary: '1',
      }

      await setPrimaryGuardian(primaryGuradianPayload)

      toast.success('Submitted successfully', {id: toastId})
      onAfterSubmit()
    } catch (error: any) {
      handleApiError(error, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  const isAlreadyLinkedGuardian = (guardianId: number | string) => {
    return linkedGuardians?.find((guardian) => guardian.parent_id === guardianId)
  }

  return (
    <Formik initialValues={formValues} onSubmit={submitHandler} validationSchema={validationSchema}>
      {({isSubmitting, resetForm, values, setFieldValue, dirty, isValid}) => {
        // const guardianOptionsAll = guardianList?.data.filter((guardian: GuardianBase) => {
        //   for (let i = 0; i < values.guardians.length; i++) {
        //     const element = values.guardians[i]
        //     return String(element.parentId) !== String(guardian.parent_id)
        //   }
        // })

        return (
          <Form>
            <FieldArray name="guardians">
              {({push, remove}) => (
                <div>
                  {values.guardians.map((_, index) => {
                    const alreadyLinked = isAlreadyLinkedGuardian(values.guardians[index].parentId)

                    return (
                      <>
                        <Row
                          key={index}
                          className="mb-4 border border-secondary p-4 rounded d-flex mx-5"
                        >
                          <Col sm="5">
                            <FormikControl
                              control="select-advanced"
                              name={`guardians[${index}].parentId`}
                              label="Guardian"
                              options={guardianList?.data}
                              optionValue="parent_id"
                              optionLabel="parent_name"
                              onChange={(selected: GuardianBase, action: any) => {
                                if (values.guardians[index].parentId === values.primaryGuardian) {
                                  setFieldValue('primaryGuardian', '')
                                }
                                setFieldValue(`guardians[${index}].parentId`, selected.parent_id)
                              }}
                              getOptionLabel={(option: GuardianBase) => {
                                return `${option.parent_name ? option.parent_name + ' - ' : ''}${
                                  option.parent_mobile
                                }`
                              }}
                              value={
                                guardianList?.data.find(
                                  (guardian: GuardianBase) =>
                                    guardian.parent_id === Number(values.guardians[index].parentId)
                                ) || ''
                              }
                              required
                              disabled={alreadyLinked}
                            />
                          </Col>
                          <Col sm="5">
                            <FormikControl
                              control="select"
                              name={`guardians[${index}].relation`}
                              label="Relation"
                              disabled={alreadyLinked}
                            >
                              <option value="">Select Relation</option>
                              <option value="father">Father</option>
                              <option value="mother">Mother</option>
                              <option value="guardian">Guardian</option>
                              <option value="other">Other</option>
                            </FormikControl>
                          </Col>
                          <Col sm="2" className="align-self-center justify-content-end d-flex">
                            <Button
                              type="button"
                              onClick={() => {
                                console.log(parentForRemoval)
                                if (isAlreadyLinkedGuardian(values.guardians[index].parentId)) {
                                  setParentForRemoval(values.guardians[index].parentId)
                                } else {
                                  remove(index)
                                }
                              }}
                              disabled={isSubmitting}
                              variant="danger"
                              size="sm"
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                        <UnlinkGuardianConfirm
                          onCancel={() => setParentForRemoval(null)}
                          onDelete={async () => {
                            const toastId = toast.loading('Removing...')
                            console.log(parentForRemoval)
                            try {
                              await deleteGuardian(values.guardians[index].parentId)
                              refetchLinkedGuardians()
                              remove(index)
                              toast.success('Removed successfully', {id: toastId})
                              setParentForRemoval(null)
                            } catch (error) {
                              toast.error('Something went wrong, try again later', {id: toastId})
                            }
                          }}
                          show={!!parentForRemoval}
                        />
                      </>
                    )
                  })}
                  {/* {values.guardians.length < 2 && ( */}
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => push({parentId: '', relation: ''})}
                      className="btn btn-secondary mb-4 mx-auto"
                      disabled={isSubmitting}
                    >
                      + Add Guardian
                    </Button>
                  </div>
                  {/* )} */}
                </div>
              )}
            </FieldArray>

            <FormikControl
              className="mx-5 w-50"
              label="Primary Guardian"
              control="select-advanced"
              options={guardianList?.data.filter((guardian: GuardianBase) => {
                return values.guardians.some(
                  (guardianForm: StudentGuardianLinkFormValues) =>
                    guardianForm.parentId === guardian.parent_id
                )
              })}
              defaultValue={
                guardianList?.data.find(
                  (guardian: GuardianBase) => String(guardian.parent_id) === values.primaryGuardian
                ) || ''
              }
              placeholder="Select Primary Guardian"
              optionLabel="parent_name"
              optionValue="parent_id"
              name="primaryGuardian"
              required
              getOptionLabel={(option: GuardianBase) => {
                return `${option.parent_name ? option.parent_name + ' - ' : ''}${
                  option.parent_mobile
                }`
              }}
            />
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn
                disabled={!dirty || !isValid || isSubmitting || values.guardians.length === 0}
              />
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default LinkGuardianForm
